<script setup lang="ts">
  // contentsTypeによって、ライブ、スケジュール、アーカイブカードをグリッド上に表示するコンポーネント
  import { LiveInfo } from "~/entities/LiveInfo";
  import { liveFilterValues, liveFilters, useFilterStore } from "~/stores/FilterStore";

  interface Props {
    page?: number;
    items?: LiveInfo[];
    loading?: boolean;
    totalPages?: number;
    emptyMessage?: string; // 空の時のメッセージ

    showHeader?: boolean;
    search?: string;
  }
  const localProps = withDefaults(defineProps<Props>(), {
    page: 1,
    items: [] as any,
    loading: false,
    totalPages: 0,
    emptyMessage: "",
    showHeader: true,
    search: "",
  });

  interface Emits {
    (event: "onBack"): void;
    (event: "update:page", page: number): void;
    (event: "update:search", search: string): void;
  }
  const emit = defineEmits<Emits>();

  const filterStore = useFilterStore();

  const isSp = isSpDisplayRef();

  const pageModel = computed<number | undefined>({
    get: () => localProps.page,
    set: value => {
      emit("update:page", value || 1);
    },
  });

  const searchModel = computed<string>({
    get: () => localProps.search || "",
    set: (value: string) => {
      emit("update:search", value);
    },
  });

  const getContents = (isFeatured: boolean) => {
    return localProps.items.filter(content => content.isFeatured === isFeatured);
  };

  const handleChangeFilter = (value: string) => {
    filterStore.livePage = 1;
    filterStore.setSelectedLiveFilter(value);
  };

  const contentWidth = getContentCardWidth();
</script>

<template>
  <div v-if="showHeader" class="mx-0 mx-sm-2">
    <OrganismsBaseCardGridHeader
      v-model:search="searchModel"
      :show-back-button="false"
      :show-search="true"
      icon="live"
      :show-icon="true"
      title="ライブ配信一覧"
      @on-back="$emit('onBack')"
    >
      <v-menu scroll-strategy="close">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            icon="$mdiFilter"
            :size="isSp ? 'small' : 'default'"
            :color="filterStore.getSelectedLiveFilter().value !== liveFilterValues.All ? 'primary' : 'default'"
          />
        </template>
        <v-sheet class="bg-monochrome-04">
          <div class="py-2">
            <div
              v-for="item in liveFilters"
              :key="item.value"
              class="d-flex justify-space-between cursor-pointer py-2 px-3"
              @click="handleChangeFilter(item.value)"
            >
              <div class="pr-8">{{ item.title }}</div>
              <div>{{ item.value === filterStore.selectedLiveFilter ? "✔" : "" }}</div>
            </div>
          </div>
        </v-sheet>
      </v-menu>
    </OrganismsBaseCardGridHeader>
  </div>
  <div v-if="getContents(true).length" class="mx-2 pt-4">
    <div class="d-flex align-center pb-2" :class="{ 'px-4': isSp }">
      <v-icon icon="$mdiPin" class="mr-1" :size="20" />
      <span class="text-subtitle-1 font-weight-bold mb-1">固定</span>
    </div>
    <div v-for="featured in getContents(true)" :key="featured.id" class="pb-3 text-center">
      <OrganismsStreamingCard :live-info="featured" :loading="loading" :is-featured="true" />
    </div>
    <v-divider v-if="getContents(true).length && getContents(false).length" class="mb-6" />
  </div>

  <OrganismsBaseCardGrid
    v-model:page="pageModel"
    :total-pages="totalPages"
    :loading="loading"
    :empty-message="items.length ? undefined : emptyMessage"
    :content-width="contentWidth"
  >
    <OrganismsStreamingCard v-for="item in getContents(false)" :key="item.id" :live-info="item" />
  </OrganismsBaseCardGrid>
</template>
